/**
 * Erfassungsquelle „web" (Original-Linie): same-origin-iframe.
 *
 * Die Kundenanwendung läuft im iframe (same-origin auf Stage/Prod, §2.1);
 * lokal ist sie cross-origin — Screenshot per getDisplayMedia funktioniert
 * trotzdem, die Route-Auslesung liefert dann null (Dev-Fallback laut §9).
 *
 * Der Screenshot wird auf den iframe-Bereich zugeschnitten
 * (SameOriginCapture) und direkt hochgeladen — Annotationen gehen bei
 * Bedarf nachträglich über „Bearbeiten" (Fenster-Ansicht der Kern-Seite).
 */

import { useEffect, useRef, useState } from "react"
import { Camera, ImageUp } from "lucide-react"
import { toast } from "sonner"
import { SameOriginCapture } from "@/lib/screenshot"
import type { Application } from "@/types"

export interface WebIframeSourceProps {
  anwendung: Application
  /** Aktive Stufen-URL (Kern: `webUrl()` — live nur mit gepflegter urlLive) */
  url: string
  /** Umgebung der Meldung (Zielbild #36): dev = Stage, live = echte Daten */
  umgebung: "live" | "dev"
  onUmgebungChange: (u: "live" | "dev") => void
  /** null = noch kein Entwurf → „Melden"-Button; sonst Screenshot-Werkzeuge */
  entwurfId: number | null
  /** Entwurf beginnen (Kern-Mutation `beginnen`) */
  meldenStarten: () => void
  meldenLaeuft: boolean
  /** Aufgenommenes Bild — der Kern lädt es direkt hoch (web-Verhalten) */
  onBild: (bild: Blob) => void
  /** Fallback „Bilddatei hochladen" — öffnet den Datei-Dialog des Kerns */
  onDateiWaehlen: () => void
  /** Der Kern liest darüber die iframe-Route für den Kontext (F8) */
  routeLeserRef: React.MutableRefObject<(() => string | null) | null>
}

export function WebIframeSource({
  anwendung,
  url,
  umgebung,
  onUmgebungChange,
  entwurfId,
  meldenStarten,
  meldenLaeuft,
  onBild,
  onDateiWaehlen,
  routeLeserRef,
}: WebIframeSourceProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Die aktive Stufen-URL kommt vom Kern (Zielbild #36): ein Tester bedient
  // Stage UND Live derselben Anwendung; der Umschalter im Rahmenkopf wechselt
  // die iframe-src. Nur anbieten, wenn beide Stufen gepflegt sind.
  const liveVerfuegbar = !!anwendung.urlLive && anwendung.urlLive !== anwendung.url

  // Persistente Tab-Verbindung (Port aus rmnc-ninox-tester): Freigabe-Dialog
  // nur beim ersten Screenshot, danach sofortige Frame-Abgriffe
  const captureRef = useRef<SameOriginCapture | null>(null)
  if (captureRef.current === null) captureRef.current = new SameOriginCapture()
  const [tabVerbunden, setTabVerbunden] = useState(false)
  useEffect(() => {
    const capture = captureRef.current!
    capture.onEnded = () => setTabVerbunden(false)
    return () => capture.trennen()
  }, [])

  // F8: Route der eingebetteten Anwendung für den Kontext des Kerns
  useEffect(() => {
    routeLeserRef.current = () => {
      try {
        // Same-origin (Stage/Prod §2.1): Route direkt aus dem iframe
        return iframeRef.current?.contentWindow?.location.pathname ?? null
      } catch {
        return null // lokal cross-origin → Dev-Fallback (§9)
      }
    }
    return () => {
      routeLeserRef.current = null
    }
  }, [routeLeserRef])

  const screenshotAufnehmen = async () => {
    const capture = captureRef.current!
    try {
      if (!capture.verbunden) {
        await capture.verbinden()
        setTabVerbunden(true)
      }
      // Nur den iframe-Bereich (die Kundenanwendung) übernehmen — nicht die
      // Tester-UI. Direkt anhängen, ohne Editor-Zwischenschritt; Annotationen
      // gehen bei Bedarf nachträglich über „Bearbeiten" (Fenster-Ansicht).
      const bild = await capture.frameAbgreifen(iframeRef.current?.getBoundingClientRect() ?? null)
      onBild(bild)
    } catch (e) {
      // Abbruch im Freigabe-Dialog ist kein Fehler
      if ((e as Error).name === "NotAllowedError") return
      if (capture.verbunden) {
        toast.error("Screenshot fehlgeschlagen")
      } else {
        setTabVerbunden(false)
        toast.error("Tab-Verbindung verloren — der nächste Klick verbindet neu")
      }
    }
  }

  return (
    <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
      {/* Browser-Chrome-Kopf (Design Screen 03) */}
      <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-muted px-4">
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="ml-2 truncate text-xs text-muted-foreground/80">
          {url.replace(/^https?:\/\//, "")}
        </span>
        {/* Stufen-Umschalter (Zielbild #36) — nur bei gepflegter Live-URL.
            Wechsel lädt den Rahmen neu; während einer laufenden Meldung
            gesperrt, damit Screenshots und Kontext zur Stufe passen. */}
        {liveVerfuegbar && (
          <div className="ml-auto flex items-center rounded-full border bg-card p-0.5 text-[11px] font-semibold">
            {(
              [
                ["dev", "Stage"],
                ["live", "Live"],
              ] as const
            ).map(([wert, label]) => (
              <button
                key={wert}
                type="button"
                onClick={() => onUmgebungChange(wert)}
                disabled={entwurfId !== null && umgebung !== wert}
                title={
                  entwurfId !== null && umgebung !== wert
                    ? "Während einer laufenden Meldung ist die Umgebung fest"
                    : wert === "live"
                      ? "Echte Daten (Produktionsumgebung)"
                      : "Testumgebung (Stage)"
                }
                className={`rounded-full px-2.5 py-0.5 transition-colors ${
                  umgebung === wert
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground disabled:opacity-40"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <iframe
        ref={iframeRef}
        src={url}
        title={anwendung.name}
        className="min-h-0 w-full flex-1"
      />
      {/* Schwebende Melden-Leiste (§8) — bewusst dezent: links unten,
          nur Symbole, damit die Anwendung im Mittelpunkt bleibt */}
      <div className="absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full border bg-card p-1.5 shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
        {entwurfId === null ? (
          <button
            type="button"
            onClick={meldenStarten}
            disabled={meldenLaeuft}
            className="rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            Melden
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => void screenshotAufnehmen()}
              title={
                tabVerbunden
                  ? "Screenshot aufnehmen — Tab verbunden, sofort ohne Freigabe-Dialog"
                  : "Screenshot aufnehmen — der erste fragt einmalig die Tab-Freigabe ab"
              }
              aria-label="Screenshot aufnehmen"
              className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              <Camera className="h-4 w-4" />
              {tabVerbunden && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-emerald-400" />
              )}
            </button>
            <button
              type="button"
              onClick={onDateiWaehlen}
              title="Bilddatei hochladen"
              aria-label="Bilddatei hochladen"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground/70 hover:bg-secondary/80 hover:text-foreground"
            >
              <ImageUp className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
